import React from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import {
  TextField,
  Button,
  Typography,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import LogoutIcon from "@mui/icons-material/Logout";
import { useNavigate, useParams } from "react-router-dom";
import { getLoginUserData, logoutUser } from "../../services/account.service";
import Loader from "../../common/loader";
import { useUser } from "../../context/UserContext";

interface AccountProps {
  userId: string;
  loggedInUserId?: string;
  getProfile: (id: string) => Promise<any>;
  updateProfile: (id: string, data: Partial<FormData>) => Promise<any>;
  title: string;
  subtitle: string;
  showSignature?: boolean;
}

interface FormData {
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  phone: string;
  street: string;
  city: string;
  state: string;
  zipcode: string;
}

const defaultSignatureIcon = "https://cdn.icon-icons.com/icons2/1765/PNG/512/hand-outline-gesture-7_113971.png";

const Account: React.FC<AccountProps> = ({
  userId,
  loggedInUserId,
  getProfile,
  updateProfile,
  title,
  subtitle,
  showSignature = false,
}) => {
  const navigate = useNavigate();
  const { setUser } = useUser();
  const { businessName } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [formData, setFormData] = React.useState<FormData>({
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    phone: "",
    street: "",
    city: "",
    state: "",
    zipcode: "",
  });

  const [signatureImageUrl, setSignatureImageUrl] = React.useState(defaultSignatureIcon);
  const [loading, setLoading] = React.useState(true);
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState("");
  const [alertSeverity, setAlertSeverity] = React.useState<
    "success" | "error" | "info"
  >("success");

  React.useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        if (userId) {
          await loadProfile(userId);
        }
      } catch (err: any) {
        setAlertSeverity("error");
        setAlertMessage(err?.message || "Failed to load user data.");
        setAlertOpen(true);
      } finally {
        setLoading(false);
      }
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProfile = async (id: string) => {
    try {
      const user = await getProfile(id);
      if (user) {
        setFormData({
          firstName: user.firstName || "",
          middleName: user.middleName || "",
          lastName: user.lastName || "",
          email: user.email || "",
          phone: user.phone || "",
          street: user.street || "",
          city: user.city || "",
          state: user.state || "",
          zipcode: user.zipcode || "",
        });
        const loginUser = await getLoginUserData();
        if(loginUser?.role === user?.role){
          setUser(user);
        }
        if (showSignature) {
          setSignatureImageUrl(
            user.signature
              ? `${user.signature}`
              : defaultSignatureIcon
          );
        }
      }
    } catch (err: any) {
      setAlertSeverity("error");
      setAlertMessage(err?.message || "Failed to load your profile.");
      setAlertOpen(true);
    }
  };

  const validationSchema = Yup.object({
    firstName: Yup.string().required("First name is required"),
    middleName: Yup.string().optional(),
    lastName: Yup.string().required("Last name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    phone: Yup.string().required("Phone number is required"),
    street: Yup.string().required("Street is required"),
    city: Yup.string().required("City is required"),
    state: Yup.string().required("State is required"),
    zipcode: Yup.string().required("Zipcode is required"),
  });

  const handleSubmit = async (values: FormData) => {
    setLoading(true);
    try {
      const updatedFields: Partial<FormData> = {};

      if (formData.firstName !== values.firstName) updatedFields.firstName = values.firstName;
      if (formData.middleName !== values.middleName) updatedFields.middleName = values.middleName;
      if (formData.lastName !== values.lastName) updatedFields.lastName = values.lastName;
      if (formData.email !== values.email) updatedFields.email = values.email;
      if (formData.phone !== values.phone) updatedFields.phone = values.phone;
      if (formData.street !== values.street) updatedFields.street = values.street;
      if (formData.city !== values.city) updatedFields.city = values.city;
      if (formData.state !== values.state) updatedFields.state = values.state;
      if (formData.zipcode !== values.zipcode) updatedFields.zipcode = values.zipcode;

      if (Object.keys(updatedFields).length === 0) {
        setAlertSeverity("info");
        setAlertMessage("No changes made.");
      } else {
        await updateProfile(userId, updatedFields);
        setAlertSeverity("success");
        setAlertMessage("Your profile updated successfully!");
        await loadProfile(userId); // Refresh data
      }
    } catch (error: any) {
      setAlertSeverity("error");
      setAlertMessage(error?.message || "Failed to update your profile.");
    } finally {
      setAlertOpen(true);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logoutUser();
    navigate(`/${businessName}/login`);
  };

  if (loading) return <Loader />;

  return (
    <div>
      <div
        className={`max-w-full ${isMobile ? 'p-2' : 'p-4'} rounded-xl border border-gray-200`}
      >
        {/* Header */}
        <div className={`flex flex-col sm:flex-row justify-between items-center ${isMobile ? 'gap-2' : 'gap-4'} mb-6`}>
          <div className="text-center sm:text-left">
            <Typography variant="h5" className="font-bold text-gray-800">
              {title}
            </Typography>
            <Typography variant="subtitle2" className="text-gray-600">
              {subtitle}
            </Typography>
          </div>
          {showSignature && (
            <img
              src={signatureImageUrl}
              onError={(e) => {
                const target = e.currentTarget as HTMLImageElement;
                target.onerror = null;
                target.src = defaultSignatureIcon;
              }}
              alt="Signature"
              className="rounded-md border border-gray-300 bg-white"
              style={{ width: "auto", height: isMobile ? "50px" : "70px", objectFit: "contain" }}
            />
          )}
        </div>

        <Formik
          initialValues={formData}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, errors, touched }) => (
            <Form className="space-y-8">
              <Box className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field
                  name="firstName"
                  as={TextField}
                  label="First Name"
                  fullWidth
                  error={Boolean(touched.firstName && errors.firstName)}
                  helperText={touched.firstName && errors.firstName}
                  size="medium" InputProps={{style: {borderRadius: 10}}}
                />
                <Field
                  name="middleName"
                  as={TextField}
                  label="Middle Name (optional)"
                  fullWidth
                  error={Boolean(touched.middleName && errors.middleName)}
                  helperText={touched.middleName && errors.middleName}
                  size="medium" InputProps={{style: {borderRadius: 10}}}
                />
                <Field
                  name="lastName"
                  as={TextField}
                  label="Last Name"
                  fullWidth
                  error={Boolean(touched.lastName && errors.lastName)}
                  helperText={touched.lastName && errors.lastName}
                  size="medium" InputProps={{style: {borderRadius: 10}}}
                />
                <Field
                  name="email"
                  as={TextField}
                  label="Email"
                  fullWidth
                  error={Boolean(touched.email && errors.email)}
                  helperText={touched.email && errors.email}
                  size="medium" InputProps={{style: {borderRadius: 10}}}
                />
                <Field
                  name="phone"
                  as={TextField}
                  label="Phone"
                  fullWidth
                  error={Boolean(touched.phone && errors.phone)}
                  helperText={touched.phone && errors.phone}
                  size="medium" InputProps={{style: {borderRadius: 10}}}
                />
                <Field
                  name="city"
                  as={TextField}
                  label="City"
                  fullWidth
                  error={Boolean(touched.city && errors.city)}
                  helperText={touched.city && errors.city}
                  size="medium" InputProps={{style: {borderRadius: 10}}}
                />
                <Field
                  name="street"
                  as={TextField}
                  label="Street"
                  fullWidth
                  error={Boolean(touched.street && errors.street)}
                  helperText={touched.street && errors.street}
                  size="medium" InputProps={{style: {borderRadius: 10}}}
                />
                <Field
                  name="state"
                  as={TextField}
                  label="State"
                  fullWidth
                  error={Boolean(touched.state && errors.state)}
                  helperText={touched.state && errors.state}
                  size="medium" InputProps={{style: {borderRadius: 10}}}
                />
                <Field
                  name="zipcode"
                  as={TextField}
                  label="Zipcode"
                  fullWidth
                  error={Boolean(touched.zipcode && errors.zipcode)}
                  helperText={touched.zipcode && errors.zipcode}
                  size="medium" InputProps={{style: {borderRadius: 10}}}
                />
              </Box>
              {/* Submit & Logout */}
              <Box className={`flex ${isMobile ? 'flex-col' : 'flex-row'} justify-end gap-4`}>
                {(!loggedInUserId || userId === loggedInUserId) && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<LogoutIcon />}
                    onClick={handleLogout}
                    sx={{ borderRadius: 2, fontWeight: 600 }}
                  >
                    Logout
                  </Button>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={
                    loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  sx={{ borderRadius: 2, fontWeight: 600 }}
                  disabled={loading}
                >
                  Save Profile
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </div>

      {/* Snackbar */}
      <Snackbar
        open={alertOpen}
        autoHideDuration={4000}
        onClose={() => setAlertOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setAlertOpen(false)}
          severity={alertSeverity}
          sx={{ width: "100%" }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Account;
